import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { assert } from 'ts-essentials';
import i18n from '@i18next/i18n';
import { UiDataService } from '@src/slide/domain/ui-model/service/ui-data-service';
import PresentationModel from '@src/slide/domain/ui-model/element-modeles/presentation-model';
import Button from '@tencent/dui-mobile/lib/components/Button';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISlideDataModel } from '@tencent/slide-sdk-model';
import ScaleSlide from '@editor/src/page/index/components/canvas/scale-slide';
import { RenderPostionType } from '@src/js/constant';
import styles from './replace-slide-content.less';
import tickIcon from '@tencent/docs-design-resources/icon/desktop/svg/tick_white_outline_light_24.svg';
import restoreIcon from '@tencent/docs-design-resources/icon/desktop/svg/restore_24.svg';
import { changeSlideSize, replaceSlide } from '../common/utils';
import { ReplaceSlideContentLoading } from '../common/loading';
import { ReplaceSlideContentFail } from '../common/fail';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IAIAssistantService } from '@src/slide/application/service/ai-ssistant/ai-ssistant-service.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';

const GENERATE_NUMBER = 4;

interface IAddSlideCardProps {
    onClose: () => void;
    startCurrentId: string; // 记录组件唤起时的选中页，以定制协同编辑选中页被删除时的表现
    sourceSlideModel?: ISlideModel;
    notShowTitle?: boolean;
    uiDataService: IUiDataService;
    aiAssistantService: IAIAssistantService,
}

// 重新生成图片弹窗
export const ReplaceSlideContent = ({
    onClose,
    startCurrentId,
    sourceSlideModel,
    notShowTitle,
    uiDataService,
    aiAssistantService,
}: IAddSlideCardProps) => {
    const [selectedId, setSelectedId] = useState('');
    const [refreshCount, setRefreshCount] = useState(0);
    const [contentState, setContentState] = useState('loading');
    const [cloneUiDataService, setCloneUiDataService] = useState<UiDataService | null>();

    useEffect(() => {
        const currentPresentation = uiDataService.presentation;
        const currentSlide = sourceSlideModel ?? currentPresentation.selectionService?.currentSlide;
        if (!currentSlide?.sdkData) {
            return;
        }
        setCloneUiDataService(undefined);
        aiAssistantService?.restructureSlide(currentSlide.sdkData as ISlideDataModel, GENERATE_NUMBER).then((data) => {
            if (!data) {
                setCloneUiDataService(null);
                return;
            }
            // TODO: data 修改比例和 AI 模板一致，这个应该迁移到组件里完成
            data.sldSz.cx = 12193200;
            data.sldSz.cy = 6858000;
            data.sldSz.type = 15;

            const PresentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
            assert(PresentationStoreService, 'PresentationStoreService is not ready');
            const clonePresentationStoreService = PresentationStoreService.createFromData(data);
            const presentation = clonePresentationStoreService.getPresentation();
            assert(presentation, 'create ui presentation failed');

            const uiDataService = new UiDataService();
            uiDataService.presentation = new PresentationModel({});
            uiDataService.presentation.asyncConvertChildren = false;
            uiDataService.presentation.bindSDK(presentation);
            uiDataService.presentation?.children?.forEach((children) => {
                children.startConvertChildrenTask();
            });

            // 修改比例
            changeSlideSize(currentPresentation, uiDataService.presentation);

            setSelectedId('');
            setCloneUiDataService(uiDataService);
        });
    }, [refreshCount, startCurrentId]);

    useEffect(() => {
        if (cloneUiDataService === undefined) {
            setContentState('loading');
            return;
        }
        if (cloneUiDataService === null || cloneUiDataService.presentation.children.length <= 0) {
            setContentState('fail');
            return;
        }
        setContentState('success');
    }, [cloneUiDataService]);

    const replace = () => {
        if (!cloneUiDataService?.presentation) {
            return;
        }
        replaceSlide(cloneUiDataService?.presentation, selectedId, startCurrentId, uiDataService, sourceSlideModel);
        onClose();
    };

    const wrapperWidth = () => (Math.min(window.innerWidth - 32, 400) / 2) - 14;

    if (contentState === 'loading') {
        return <ReplaceSlideContentLoading tipMsg={i18n.t('AI单页美化中...')} />;
    }
    if (contentState === 'fail') {
        return <ReplaceSlideContentFail tipMsg={i18n.t('很抱歉，当前页内容不足，无法美化')} />;
    }

    return (
        <div className={styles.container}>
            <div className={styles['slide-container']}>
                <div className={styles.description}>{ notShowTitle ?  i18n.t('页面选择') : i18n.t('AI 单页美化') }</div>
                <div
                    className={classNames({
                        [styles['image-galley']]: true,
                    })}
                    style={{ minWidth: (2 * wrapperWidth()) + 8 }} // 只有一个图的时候看上去别太窄就行
                >
                    {cloneUiDataService?.presentation.children.slice(0, 4).map((slide, index) => (
                        <div
                            className={classNames({
                                [styles['slide-item']]: true,
                                [styles['slide-item-hover']]: slide?.key && cloneUiDataService,
                                [styles['slide-item-selected']]: slide?.id === selectedId,
                            })}
                            key={`${slide?.key}${index}`}
                            onClick={() => setSelectedId(slide?.id ?? '')}
                        >
                            {slide?.key && cloneUiDataService ? (
                                <ScaleSlide
                                    width={wrapperWidth()}
                                    height={(wrapperWidth() / 16) * 9}
                                    isShown={true}
                                    slideModel={slide}
                                    uiDataService={cloneUiDataService}
                                    shouldUpdate={false}
                                    fromScale={0}
                                    region={RenderPostionType.PAGINGSELECTION}
                                    isTemplate={true}
                                    styles={{
                                        scaleSlideStyle: {
                                            top: 0,
                                            left: 0,
                                            position: 'relative',
                                            borderRadius: 4,
                                            width: wrapperWidth(),
                                            overflow: 'hidden',
                                        },
                                    }}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.footer}>
                <Button
                    className={styles['ai-button-normal']}
                    onClick={() => {
                        setRefreshCount(refreshCount + 1);
                        setSelectedId('');
                        tdw({
                            opername: TDWOperationType.DOC_PPT_AI,
                            module: TDWModuleType.PAGE_BEAUTY,
                            action: TDWActionType.SWITCH,
                            ver5: TDWModuleType.MOBILE,
                        });
                    }}
                >
                    <img src={restoreIcon} />
                    {i18n.t('换一批')}
                </Button>
                <Button
                    className={classNames({
                        [styles['ai-button-primary']]: true,
                        [styles['ai-button-disabled']]: !selectedId,
                    })}
                    onClick={() => {
                        replace();
                        tdw({
                            opername: TDWOperationType.DOC_PPT_AI,
                            module: TDWModuleType.PAGE_BEAUTY,
                            action: TDWActionType.APPLY,
                            ver5: TDWModuleType.MOBILE,
                        });
                    }}
                    disabled={!selectedId}
                >
                    <img src={tickIcon} />
                    {i18n.t('替换选中页')}
                </Button>
            </div>
        </div>
    );
};
